import { useCallback, useEffect, useRef } from 'react';
import { AppState, Platform, type AppStateStatus } from 'react-native';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { canRunOperationalBackgroundWork } from '../accountSetupService';
import { EMPLOYEE_PERMISSIONS_FRESHNESS_POLL_MS } from '../sessionCacheTtl';
import { checkAndRefreshEmployeePermissionsFreshness } from '../employeePermissionsFreshnessService';

/**
 * Poll + AppState: si el dueño revoca/cambia permisos, el dispositivo del empleado
 * re-bootstrapa y AppNavigator soft-blockea (NoBusinessAccessScreen).
 */
export function useEmployeePermissionsFreshness(): void {
  const userId = useAuthStore((s) => s.session?.user?.id);

  const refresh = useCallback(async () => {
    if (!userId || !canRunOperationalBackgroundWork()) return;
    try {
      await checkAndRefreshEmployeePermissionsFreshness(userId);
    } catch (e) {
      console.warn('[useEmployeePermissionsFreshness]', e);
    }
  }, [userId]);

  useEffect(() => {
    if (userId && canRunOperationalBackgroundWork()) {
      void refresh();
    }
  }, [refresh, userId]);

  const visibleRef = useRef(true);
  useEffect(() => {
    const onActive = () => {
      if (canRunOperationalBackgroundWork()) void refresh();
    };
    const sub = AppState.addEventListener('change', (next: AppStateStatus) => {
      visibleRef.current = next === 'active';
      if (next === 'active') onActive();
    });
    let removeVis: (() => void) | undefined;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVis = () => {
        visibleRef.current = document.visibilityState === 'visible';
        if (visibleRef.current) onActive();
      };
      document.addEventListener('visibilitychange', onVis);
      visibleRef.current = document.visibilityState === 'visible';
      removeVis = () => document.removeEventListener('visibilitychange', onVis);
    }
    const interval = setInterval(() => {
      if (visibleRef.current) onActive();
    }, EMPLOYEE_PERMISSIONS_FRESHNESS_POLL_MS);
    return () => {
      sub.remove();
      removeVis?.();
      clearInterval(interval);
    };
  }, [refresh]);
}
